import { useState, useEffect, useCallback } from "react";
import { AccountType, Store, StoreRSInfo } from "../../../types";

import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import { MenuItem } from "@mui/material";
import FormControlLabel from '@mui/material/FormControlLabel';

import { createLog, editStoreRSInfo, getCSVAccountData } from "../../../lib/admin";
import NoImage from '../../../assets/NoImage.jpg';
import { StoreAccountDropdown } from "./StoreAccountDropdown";
import StickySaveBar from "../../global/StickySaveBar";
import Notification from "../../global/Notification";
import { useHasScope } from "../../../utils/Permission";
import retailIcon from "../../../assets/management/retail.svg";

type Props =
    {
        store: Store,
        hasUnsavedChanges: boolean,
        setHasUnsavedChanges: (flag: boolean) => void,
        hasBeenUpdated: boolean,
        setHasBeenUpdated: (flag: boolean) => void
    }

const planOptions = [
    { value: '0', label: 'Unknown' },
    { value: '1', label: 'Bronze' },
    { value: '2', label: 'Silver' },
    { value: '3', label: 'Gold' },
    { value: '4', label: 'Platinum' },
    { value: '5', label: 'Kiosk' },
    { value: '6', label: 'Brochure' }
];

const statusOptions = [
    { value: 0, label: 'Offline' },
    { value: 1, label: 'In Progress' },
    { value: 2, label: 'Live' },
];

const DOC_URL =
    "https://retailsystem-dev.notion.site/Add-store-domain-to-allowed-CORS-domains-b5ff0a43f37c4ff0b2f0001840aafd6d?pvs=4";

type RetailSystemValues = {
    siteName: string,
    dns: string,
    liveState: number | string,
    accountGuid: string,
    accountId: number | string,
    retailSystemPlan: string,
    logo: string,
    multiStoreChecked: boolean,
    orderDestinationAccountId: number | string,
}

const valuesFromStore = (store: Store): RetailSystemValues => ({
    siteName: store.friendlyName ? store.friendlyName : "Not available",
    dns: store.dns ? store.dns : "Not available",
    liveState: store.liveState != undefined ? store.liveState : "Not available",
    accountGuid: store.accountGuid ? store.accountGuid : "Not available",
    accountId: store.accountId ? store.accountId : "Not available",
    retailSystemPlan: store.storePlan ? store.storePlan.toString() : '0',
    logo: store.logo != undefined ? store.logo : "No image selected",
    multiStoreChecked: store.isMultiStore,
    orderDestinationAccountId: store.orderDestinationAccountId ? store.orderDestinationAccountId : "Not available",
});

const isDirty = (draft: RetailSystemValues, base: RetailSystemValues) =>
    (Object.keys(base) as (keyof RetailSystemValues)[]).some((key) => String(draft[key]) !== String(base[key]));

export default function StoreRetailSystemForm({ store, setHasUnsavedChanges, setHasBeenUpdated }: Props) {
    const [baseline, setBaseline] = useState<RetailSystemValues>(() => valuesFromStore(store));
    const [drafts, setDrafts] = useState<RetailSystemValues>(baseline);
    const [logoImage, setLogoImage] = useState(NoImage);
    const [accountOptions, setAccountOptions] = useState<AccountType[]>([]);
    const [dropdownKey, setDropdownKey] = useState(0);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const hasUpdateRetailSystemInfo = useHasScope('update:rsinformation');

    const dismissSaveError = useCallback(() => setSaveError(null), []);
    const closeSuccessMessage = useCallback(() => setSuccessMessage(null), []);

    const { siteName, dns, liveState, accountGuid, accountId, retailSystemPlan, logo, multiStoreChecked, orderDestinationAccountId } = drafts;
    const hasChanges = isDirty(drafts, baseline);

    useEffect(() => {
        setHasUnsavedChanges(hasChanges);
    }, [hasChanges, setHasUnsavedChanges]);

    const updateField = <K extends keyof RetailSystemValues>(field: K, value: RetailSystemValues[K]) => {
        setDrafts((current) => ({ ...current, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (!hasUpdateRetailSystemInfo) {
            setSaveError("You do not have permission to update this store.");
            return;
        }
        const editedStore: StoreRSInfo = {
            id: store.id,
            friendlyName: store.friendlyName,
            accountGuid: store.accountGuid,
            accountId: store.accountId,
            dns: store.dns,
            storePlan: store.storePlan,
            logo: store.logo,
            isMultiStore: store.isMultiStore,
            liveState: store.liveState,
            orderDestinationAccountId: store.orderDestinationAccountId,
        }

        if (siteName !== store.friendlyName && siteName.trim() !== "" && siteName != 'Not available') editedStore.friendlyName = siteName;
        if (accountGuid !== store.accountGuid && accountGuid.trim() !== "" && accountGuid != 'Not available') editedStore.accountGuid = accountGuid;
        if (accountId !== store.accountId && accountId !== "Not available" && accountId !== "") {
            const parsedAccountId = typeof accountId === "string" ? parseInt(accountId) : accountId;
            if (!isNaN(parsedAccountId)) editedStore.accountId = parsedAccountId;
        }
        if (liveState !== store.liveState && liveState !== "Not available" && liveState !== "") {
            const parsedLiveState = typeof liveState === "string" ? parseInt(liveState) : liveState;
            if (!isNaN(parsedLiveState)) editedStore.liveState = parsedLiveState;
        }

        if (!store.storePlan || retailSystemPlan !== store.storePlan.toString()) editedStore.storePlan = parseInt(retailSystemPlan);
        if (logo !== store.logo && logo != "No image selected") editedStore.logo = logo;
        if (multiStoreChecked !== store.isMultiStore) editedStore.isMultiStore = multiStoreChecked;
        if (dns !== store.dns && dns.trim() !== "" && dns != 'Not available') editedStore.dns = dns;
        if (orderDestinationAccountId == "none")
            editedStore.orderDestinationAccountId = null;
        if (orderDestinationAccountId !== store.orderDestinationAccountId && orderDestinationAccountId !== "Not available" && orderDestinationAccountId !== "") {
            const parsedOrderId = typeof orderDestinationAccountId === "string" ? parseInt(orderDestinationAccountId) : orderDestinationAccountId;
            if (!isNaN(parsedOrderId)) editedStore.orderDestinationAccountId = parsedOrderId;
        }

        if (editedStore.friendlyName && editedStore.friendlyName.length > 50) {
            setSaveError("Friendly name cannot be longer than 50 characters.");
            return;
        }

        setSaveError(null);
        setSaving(true);
        try {
            const response = await editStoreRSInfo(editedStore);
            if (response) {
                createLog({
                    storeId: store.id,
                    entityType: 'storeSettings',
                    section: 'retailSystem',
                    action: 'save',
                    lastValue: baseline,
                    newValue: drafts,
                });
                setBaseline(drafts);
                setSuccessMessage("Changes saved successfully.");
                setHasBeenUpdated(true);
            } else {
                setSaveError("An error occurred. Please try again later.");
            }
        } catch (error) {
            console.error("Error saving RetailSystem information:", error);
            setSaveError("An error occurred. Please try again later.");
        } finally {
            setSaving(false);
        }
    };

    const handleCancelChanges = () => {
        setDrafts(baseline);
        setDropdownKey((key) => key + 1);
        setSaveError(null);
    };

    const renderLogoImage = () => {
        if (store.logo) {
            const img = new Image();
            img.src = store.logo;
            img.onload = () => {
                setLogoImage(store.logo);
            };
            img.onerror = () => {
                setLogoImage(NoImage);
            };
        } else {
            setLogoImage(NoImage);
        }
    };

    useEffect(() => {
        renderLogoImage();
    }, []);

    useEffect(() => {
        updateDropdown();
    }, [])

    const updateDropdown = async () => {
        const response = await getCSVAccountData();
        setAccountOptions(response);
    }

    const onDeletedAccount = () => {
        updateField("orderDestinationAccountId", "none");
    }

    const onSelectedAccount = (id: number) => {
        if (accountOptions) {
            const accountSelected = accountOptions.find(account => account.id === id);
            if (accountSelected) {
                updateField("orderDestinationAccountId", id);
            }
        }
    }

    return (
        <div className="sync-management">
            <div className="sync-header">
                <p>Shopify Stores/{store.friendlyName}</p>
                <h1><img src={retailIcon} alt="" />RetailSystem</h1>
            </div>
            <Divider className="divider" />
            <div className="form-container">
                <div className="column">
                    <div className="field specific-field logo">
                        <div className="image-input-container">
                            <img className="logo-image" src={logoImage} alt="Site Logo" />
                        </div>
                    </div>
                    <div className="field specific-field">
                        <div className="field-content">
                            <p className="field-label">Site Name</p>
                            <TextField
                                required
                                id="outlined-required"
                                value={siteName}
                                onChange={(e) => updateField("siteName", e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="field">
                        <p className="field-label">BigFurnitureData Store</p>
                        <div className="field-edit">
                            <FormControlLabel
                                control={
                                    <Switch checked={multiStoreChecked} onChange={() => updateField("multiStoreChecked", !multiStoreChecked)} />
                                }
                                label={multiStoreChecked ? "Yes" : "No"}
                                labelPlacement="start" />
                        </div>
                    </div>
                    <div className="field">
                        <p className="field-label">RetailSystem Plan</p>
                        <Select

                            value={retailSystemPlan}
                            onChange={(e) => updateField("retailSystemPlan", e.target.value as string)}
                        >
                            {planOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>
                    <div className="field">
                        <Box className="data-tooltips-and-labels">
                            <p className="field-label no-margin">Live Status</p>

                            <Tooltip
                                title={
                                    <span>
                                        Store must be In Progress or Live to make requests to Web Services.
                                        <br />
                                        <Link
                                            href={DOC_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                            color="inherit"
                                        >
                                            View related documentation
                                        </Link>
                                    </span>
                                }
                                arrow
                                enterTouchDelay={0}
                                leaveTouchDelay={3000}
                            >
                                <Link
                                    href={DOC_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="doc-link"
                                >
                                    <IconButton size="small">
                                        <InfoOutlinedIcon fontSize="inherit" />
                                    </IconButton>
                                </Link>
                            </Tooltip>
                        </Box>

                        <Select
                            value={liveState}
                            onChange={(e) => updateField("liveState", Number(e.target.value))}
                        >
                            {statusOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>

                </div>
                <div className="column">
                    <div className="field">
                        <p className="field-label">Store Logo</p>
                        <TextField
                            required
                            id="outlined-required"
                            value={logo}
                            onChange={(e) => updateField("logo", e.target.value)}
                        />
                    </div>
                    <div className="field">
                        <Box className="data-tooltips-and-labels">
                            <p className="field-label no-margin">DNS</p>

                            <Tooltip
                                title={
                                    <span>
                                        Include all public domains separated by commas. Contact Tech Team to
                                        refresh services after update.
                                        <br />
                                        <Link
                                            href={DOC_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                            color="inherit"
                                        >
                                            View related documentation
                                        </Link>
                                    </span>
                                }
                                arrow
                                enterTouchDelay={0}
                                leaveTouchDelay={3000}
                            >
                                <Link
                                    href={DOC_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="doc-link"
                                >
                                    <IconButton size="small">
                                        <InfoOutlinedIcon fontSize="inherit" />
                                    </IconButton>
                                </Link>
                            </Tooltip>
                        </Box>

                        <TextField
                            required
                            id="outlined-required"
                            value={dns}
                            onChange={(e) => updateField("dns", e.target.value)}
                        />
                    </div>

                    <div className="field">
                        <p className="field-label">Account Guid</p>
                        <TextField
                            required
                            id="outlined-required"
                            value={accountGuid}
                            onChange={(e) => updateField("accountGuid", e.target.value)}
                        />
                    </div>
                    <div className="field">
                        <p className="field-label">Account Id</p>
                        <TextField
                            required
                            id="outlined-required"
                            value={accountId}
                            onChange={(e) => updateField("accountId", e.target.value)}
                            type="number"
                        />
                    </div>
                    <div className="field">
                        <p className="field-label">Order Destination Account Id</p>
                        <StoreAccountDropdown
                            key={dropdownKey}
                            defaultValue={typeof baseline.orderDestinationAccountId === "number" ? baseline.orderDestinationAccountId : null}
                            accountOptions={accountOptions}
                            onSelectAccount={onSelectedAccount}
                            onDeletedAccount={onDeletedAccount}
                        />
                    </div>
                </div>
            </div>
            <StickySaveBar
                visible={hasChanges}
                saving={saving}
                onSave={handleSaveChanges}
                onCancel={handleCancelChanges}
                alignWithSelector=".leftVertical"
                errorMessage={saveError ?? undefined}
                onErrorDismiss={dismissSaveError}
            />
            <Notification message={successMessage} type="success" onClose={closeSuccessMessage} />
        </div>
    )
}